package er.prateek.crossly.game.crossword

import er.prateek.crossly.config.RevealLetterConfig
import er.prateek.crossly.persistence.RevealFrequencyState
import er.prateek.crossly.persistence.RevealPrefsStore
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn
import kotlin.time.Clock

/**
 * *How many letter reveals does the player have left today?* -- the daily half of the reveal
 * cap. The per-puzzle half lives on [CrosswordEngine.lettersRevealed] instead, since it needs
 * to reset with the puzzle attempt rather than persist.
 *
 * Deliberately pure and clock-injected, same shape as
 * [er.prateek.crossly.ads.AdFrequencyController] -- the counting ([recordRevealUsed]) and the
 * asking ([revealsRemainingToday]) are separate calls so a reveal that never actually happens
 * (dismissed rewarded ad, cancelled flow) never spends the budget.
 */
class RevealLimiter(
    private val store: RevealPrefsStore,
    private val clock: Clock = Clock.System,
    /** UTC so the cap behaves identically for every player, matching the ad and daily caps. */
    private val timeZone: TimeZone = TimeZone.UTC,
) {
    /** `Int.MAX_VALUE` stands in for "unlimited" so callers can `min()` it with the per-puzzle cap. */
    fun revealsRemainingToday(config: RevealLetterConfig): Int {
        if (config.maxPerDay <= 0) return Int.MAX_VALUE
        return (config.maxPerDay - currentState().revealsToday).coerceAtLeast(0)
    }

    /** Call only after a reveal was actually granted -- never on a cancelled or failed ad. */
    fun recordRevealUsed() {
        val state = currentState()
        store.save(state.copy(revealsToday = state.revealsToday + 1))
    }

    private fun currentState(): RevealFrequencyState {
        val stored = store.load()
        val today = todayKey()
        if (stored.dayKey == today) return stored
        val rolled = stored.copy(dayKey = today, revealsToday = 0)
        store.save(rolled)
        return rolled
    }

    private fun todayKey(): String = clock.todayIn(timeZone).toString()
}
